import * as Immutable from "immutable";
import { Response } from "resourceModels";
import recordMerger from "services/record-merger";
import { uuid } from "services/generate-uuid";
import isEqual from "lodash.isequal";
import { fromAPI } from "adapters/message";

import {
  AdaMessengerMessageRecord,
  AuthMessageRecord,
  BookMeetingMessageRecord,
  BotSwitchMessageRecord,
  CaptureMessageRecord,
  ConditionalMessageRecordCreator,
  CsatMessageRecord,
  CustomHandoffEventMessageRecord,
  DeauthMessageRecord,
  HandoffMessageRecord,
  HandoffRecipeMessageRecord,
  HTTPMessageRecord,
  JavascriptEventMessageRecord,
  LinkMessageRecord,
  ListSelectionRecord,
  LiveHandoffMessageRecord,
  LivepersonChatHandoverRecord,
  PictureMessageRecord,
  QuickRepliesMessageRecord,
  ReCaptchaRecord,
  RedirectRecord,
  SalesforceLiveAgentMessageRecord,
  SatisfactionSurveyMessageRecord,
  ScheduledBlockRecordCreator,
  ScheduledCallbackMessageRecord,
  SecureNuanceLiveAgentMessageRecord,
  ShopifyMessageRecord,
  SalesforceMessageRecord,
  MagentoMessageRecord,
  ShuffleMessageRecord,
  TextMessageRecord,
  UnsupportedMessageRecord,
  VariableOverrideMessageRecord,
  VideoMessageRecord,
  WebWindowMessageRecord,
  WidgetMessageRecord,
  ZendeskChatHandoverRecord,
  ZendeskHandoffMessageRecord,
  ZendeskLiveAgentMessageRecord,
  ZendeskLiveHandoffMessageRecord,
  ZendeskAgentWorkspaceHandoffMessageRecord
} from "./messageRecords";

import {
  getResponseIndex
} from "./variables-helpers";

export const BaseSuggestedResponse = action => Immutable.Map({
  target: action.responseIdToAdd,
  label: ""
});
export const CHARACTER_LIMIT = 320;

export class ResponseRecord extends Immutable.Record({
  /** @memberof ResponseRecord# */
  id: null,

  automaticQuickReplies: false,
  buttonLabel: Immutable.Map({}), // Multilingual clarification

  /** @memberof ResponseRecord# */
  canBeQuickReply: false,

  clarifications: true,
  clientId: null,
  created: null,

  /** @memberof ResponseRecord# */
  descriptiveString: "",

  /** @memberof ResponseRecord# */
  handle: "",

  /** @memberof ResponseRecord# */
  hasForcedQuickReplies: false,

  /** @memberof ResponseRecord# */
  hasButtonLabel: true,

  description: "",
  messages: Immutable.Map({}),

  /** @memberof ResponseRecord# */
  options: Immutable.Map({}),

  rating: null,
  reserved: false,
  tags: Immutable.List([]),
  updated: null,
  saved: true,
  new: false,
  isDirty: false,
  isValid: true,
  live: true,

  reviewable: false,

  /** @memberof ResponseRecord# */
  hasTraining: true,

  canAddMessages: true,

  /** @memberof ResponseRecord# */
  hasQuickReply: true,

  /** @memberof ResponseRecord# */
  reservedResponseType: null
}) {}

// This is the only way to define getters for Immutable.Record
// TODO MEAS-221: This will be moved to `ResourceModels/Response.js`
//  after responses reducer is refactored
Object.defineProperties(ResponseRecord.prototype, {
  // Get these getters from `Response`
  hasNewLabel: Object.getOwnPropertyDescriptor(Response.prototype, "hasNewLabel"),
  languages: Object.getOwnPropertyDescriptor(Response.prototype, "languages")
});

let newResponse;
let prevState;

const takeDirtyFields = (newResponses, lastSavedResponses, fieldNames = [
  "handle",
  "description",
  "reviewable",
  "clarifications",
  "buttonLabel",
  "tags",
  "messages"
]) => (
  newResponses.map((response) => {
    const resp = response.get("isDirty")
      ? response
      : lastSavedResponses.find(r => r.id === response.id);
    const dirtyFields = {};
    fieldNames.forEach((fieldName) => {
      dirtyFields[fieldName] = resp ? resp[fieldName] : Immutable.List();
    });

    return Immutable.Map(dirtyFields);
  })
);

let lastSavedResponses;
let lastSavedResponseIndex;

/**
 * @param {Immutable.List} messages
 * @param {String} messageId
 * @returns {Immutable.Record|undefined}
 */
function getMessageFromMessageId(messages, messageId) {
  return messages.find(m => m.id === messageId);
}

/**
 * @param {Immutable.List} messages
 * @param {String} messageId
 * @returns {Number}
 */
function getMessageIndex(messages, messageId) {
  return messages.findIndex(m => m.id === messageId);
}

/**
 * @param {Number} responseIndex
 * @param {Immutable.List} state
 * @returns {Immutable.List}
 */
export function stateWithMetaData(
  responseIndex,
  state
) {
  return state.mergeIn([responseIndex], {
    isDirty: checkIfDirty(
      responseIndex,
      state
    ),
    isValid: validateResponse(responseIndex, state)
  });
}

/**
 * Note: In each of the cases, we return the original state if we can't find the response
 * index. This reducer will be overhauled, so in the meantime, we need to confirm.
 */

/**
 * @param {Immutable.List} state
 * @param {Object} action
 * @returns {Immutable.List}
 */
export const responses = (state = Immutable.List(), action) => {
  let responseMessages;
  let sourceMessageIndex;
  let destinationMessageIndex;
  let nestedMessages;
  let messageToMove;
  let sourceMessage;
  let responseIndex;
  let isBotSwitchResponse;
  let messages;
  let nextState = state;

  switch (action.type) {
    case "GET_RESPONSES_SHALLOW_SUCCESS":

    // TODO: CXP-236 Deprecate usage of this reducer. Use `resources/response`
    // eslint-disable-next-line no-fallthrough
    case "GET_RESPONSES_SUCCESS": {
      prevState = state;

      const returnedResponses = action.response.data.responses;
      nextState = nextState.filter(response => response.new);
      returnedResponses.forEach((response, index) => {
        newResponse = createRecordFromResponse(response, prevState.get(index), {
          isDirty: !prevState.isEmpty() && prevState.getIn([index, "isDirty"]),
          isValid: prevState.getIn([index, "isValid"]),
          live: response.live
        });
        nextState = nextState.push(newResponse);
      });

      lastSavedResponses = nextState;

      // merge in unsaved responses
      if (nextState.size === prevState.size) {
        nextState = nextState.mergeWith(
          (response, dirtyFields) => response.merge(dirtyFields),
          takeDirtyFields(prevState, lastSavedResponses, [
            "handle",
            "description",
            "reviewable",
            "clarifications",
            "buttonLabel",
            "tags",
            "messages"
          ])
        );
      }

      return nextState;
    }


    case "DELETE_RESPONSE":
      return nextState.filter(response => response.id !== action.responseId);

    case "CREATE_RESPONSE_SUCCESS":
      newResponse = createRecordFromResponse(
        action.response.data.response,
        nextState.find(response => response.id === action.responseId)
      );
      responseIndex = getResponseIndex(nextState, newResponse.id);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        newResponse.id
      );

      nextState = nextState.unshift(newResponse);
      lastSavedResponses = lastSavedResponses.unshift(newResponse);

      nextState = nextState.filter(response => response.id !== action.oldId);
      lastSavedResponses = lastSavedResponses.filter(
        response => response.id !== action.oldId
      );

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "SAVE_RESPONSE_SUCCESS":
      newResponse = createRecordFromResponse(
        action.response.data.response,
        nextState.find(response => response.id === action.responseId)
      );
      responseIndex = getResponseIndex(nextState, newResponse.id);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        newResponse.id
      );

      nextState = nextState.mergeIn([responseIndex], newResponse);
      lastSavedResponses = lastSavedResponses.mergeIn(
        [lastSavedResponseIndex],
        newResponse
      );
      prevState = undefined;

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "UPDATE_RESPONSE_STATUS_REQUEST":
    case "UPDATE_RESPONSE_STATUS_SUCCESS":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );
      nextState = nextState.mergeIn([responseIndex], { live: action.responseStatus });

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "UPDATE_RESPONSE":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      nextState = nextState.mergeIn([responseIndex], action.payload);

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "DELETE_RESPONSE_SUCCESS":
      return nextState.filter(
        response => response.id !== action.response.data.response._id
      );

    case "MOVE_RESPONSE_MESSAGE_TO_NESTED_MESSAGE":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      // List of response messages in the active language
      responseMessages = nextState.getIn([responseIndex, "messages", action.language]);

      // the message record that we're going to move
      messageToMove = getMessageFromMessageId(responseMessages, action.messageToMoveId);
      // the index of the message we are dropping into
      destinationMessageIndex = getMessageIndex(responseMessages, action.destinationMessageId);

      // list of nested messages
      nestedMessages = responseMessages.getIn([
        destinationMessageIndex,
        ...action.destinationNestedMessageType
      ]);

      // insert messageToMove into nestedMessages
      nestedMessages = nestedMessages.splice(action.toNestedIndex, 0, messageToMove);

      // Insert nested message List into response message
      responseMessages = responseMessages.setIn([
        destinationMessageIndex, ...action.destinationNestedMessageType
      ], nestedMessages);

      // remove message from response messages
      responseMessages = responseMessages.filter(m => m.id !== action.messageToMoveId);

      // update state
      nextState = nextState.setIn([responseIndex, "messages", action.language], responseMessages);

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "MOVE_NESTED_MESSAGE_TO_RESPONSE_MESSAGE":
      responseIndex = getResponseIndex(state, action.responseId);

      if (!responseIndex) {
        return state;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      // List of response messages in the active language
      responseMessages = nextState.getIn([responseIndex, "messages", action.language]);

      sourceMessageIndex = getMessageIndex(responseMessages, action.sourceMessageId);

      // list of nested messages
      nestedMessages = responseMessages.getIn([
        sourceMessageIndex,
        ...action.sourceNestedMessageType
      ]);

      // the message record that we're going to move
      messageToMove = getMessageFromMessageId(nestedMessages, action.messageToMoveId);
      // the message that we're dragging out of
      sourceMessage = getMessageFromMessageId(responseMessages, action.sourceMessageId);

      // remove messageToMove from nestedMessages
      nestedMessages = nestedMessages.filter(m => m.id !== action.messageToMoveId);
      // update nested messages list in the response message that we're dragging out of.
      sourceMessage = sourceMessage.setIn(action.sourceNestedMessageType, nestedMessages);

      // update source message in response messages
      responseMessages = responseMessages.set(sourceMessageIndex, sourceMessage);
      // add messageToMove to response messages at the index we dropped into
      responseMessages = responseMessages.insert(action.toIndex, messageToMove);

      // update state
      nextState = nextState.setIn([responseIndex, "messages", action.language], responseMessages);

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "ADD_SUGGESTED_RESPONSE": {
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      const optionToPush = BaseSuggestedResponse(action);

      if (nextState.getIn([responseIndex, "options"]).has(action.language)) {
        nextState = nextState.updateIn(
          [responseIndex, "options", action.language],
          arr => arr.push(optionToPush)
        );
      } else {
        nextState = nextState.setIn(
          [responseIndex, "options", action.language],
          Immutable.List([optionToPush])
        );
      }

      return stateWithMetaData(
        responseIndex,
        nextState
      );
    }

    case "DELETE_SUGGESTED_RESPONSE": {
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      nextState = nextState.updateIn([responseIndex, "options", action.language], arr => (
        arr.filter(option => option.get("target") !== action.responseIdToDelete)
      ));

      return stateWithMetaData(
        responseIndex,
        nextState
      );
    }

    case "REORDER_SUGGESTED_RESPONSES":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      nextState = nextState.updateIn(
        [responseIndex, "options", action.language],
        (arr) => {
          // TODO: Remove selectedResponses once API has been fixed.
          // This is a fix for when an Answer is deleted and it is not removed from
          // the options array of other answers.
          // We therefore need to filter the options to only use those that exist
          const selectedResponses = arr.filter(option => nextState.find(response => response.id === option.get("target")));

          const movedOption = selectedResponses.getIn([action.spliceFrom]);

          return selectedResponses
            .splice(action.spliceFrom, 1)
            .splice(action.spliceTo, 0, movedOption);
        }
      );

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "UPDATE_SUGGESTED_RESPONSE": {
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      const optionsArray = nextState.getIn([
        responseIndex,
        "options",
        action.language
      ]);

      const optionIndex = optionsArray.findIndex(
        option => option.get("target") === action.responseIdToModify
      );
      nextState = nextState.setIn(
        [responseIndex, "options", action.language, optionIndex, "label"],
        action.value || null
      );

      return stateWithMetaData(
        responseIndex,
        nextState
      );
    }

    case "SET_RESPONSE_BUTTON": {
      const { responseId, buttonLabel } = action.payload;
      responseIndex = getResponseIndex(nextState, responseId);

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        responseId
      );

      /*
        Default clarification language to english. May want to change this to
        reflect a default language
      */
      const buttonLanguageToSet = action.payload.languageCode;

      nextState = nextState.setIn([responseIndex, "buttonLabel", buttonLanguageToSet], buttonLabel);

      return stateWithMetaData(
        responseIndex,
        nextState
      );
    }

    case "DELETE_RESPONSE_BUTTON_LANGUAGE": {
      const { responseId, languageCode } = action;

      responseIndex = getResponseIndex(nextState, responseId);

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        responseId
      );

      nextState = nextState.deleteIn([responseIndex, "buttonLabel", languageCode]);

      return stateWithMetaData(
        responseIndex,
        nextState
      );
    }

    case "CREATE_TAG_SUCCESS":
      if (!action.responseRecord.get("new")) {
        responseIndex = getResponseIndex(nextState, action.responseRecord.get("id"));

        lastSavedResponseIndex = getResponseIndex(
          lastSavedResponses,
          action.responseRecord.get("id")
        );

        nextState = nextState.updateIn([responseIndex, "tags"], arr => arr.push(action.response.data.tag._id));

        return stateWithMetaData(
          responseIndex,
          nextState
        );
      }

      return state;

    case "REMOVE_TAG":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      nextState = nextState.updateIn([responseIndex, "tags"], arr => arr.filter(tagId => tagId !== action.tagId));

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "ADD_TAG_TO_RESPONSE":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      nextState = nextState.updateIn([responseIndex, "tags"], arr => arr.push(action.tagId));

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "UNDO_RESPONSE": {
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      const revertedResponse = lastSavedResponses.getIn([lastSavedResponseIndex]);

      nextState = nextState.setIn([responseIndex], revertedResponse);

      return stateWithMetaData(
        responseIndex,
        nextState
      );
    }

    case "POST_TEST-HTTP_REQUEST":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      return nextState.setIn(
        [
          ...action.keyPath,
          "httpResponseLoading"
        ],
        true
      );

    case "POST_TEST-HTTP_SUCCESS":
    case "POST_TEST-HTTP_FAILURE":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      nextState = nextState.setIn(
        [
          ...action.keyPath,
          "httpResponseLoading"
        ],
        false
      );

      return nextState.setIn(
        [
          ...action.keyPath,
          "httpResponse",
          action.requestType
        ],
        action.response
      );

    case "ADD_LANGUAGE_TO_RESPONSE_MESSAGES":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      // TODO: There will soon be a type field on responses and we should use that.
      isBotSwitchResponse = !nextState.getIn([
        responseIndex,
        "canAddMessages"
      ]);

      messages = isBotSwitchResponse
        ? Immutable.List([
          new BotSwitchMessageRecord()
        ])
        : action.messages;

      nextState = nextState
        .setIn([responseIndex, "messages", action.language], messages)
        .setIn([responseIndex, "options", action.language], Immutable.List([]));

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "ADD_BUTTON_LABEL_LANGUAGE_TO_RESPONSE":
      responseIndex = getResponseIndex(nextState, action.responseId);

      if (!responseIndex) {
        return nextState;
      }

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        action.responseId
      );

      nextState = nextState
        .setIn([responseIndex, "buttonLabel", action.language], "");

      return stateWithMetaData(
        responseIndex,
        nextState
      );

    case "DELETE_LANGUAGE": {
      const { responseId, languageCode } = action;

      responseIndex = getResponseIndex(nextState, responseId);

      lastSavedResponseIndex = getResponseIndex(
        lastSavedResponses,
        responseId
      );

      nextState = nextState
        .deleteIn([responseIndex, "messages", languageCode])
        .deleteIn([responseIndex, "options", languageCode]);

      return stateWithMetaData(
        responseIndex,
        nextState
      );
    }

    default:
      return nextState;
  }
};

/**
 * @param {Object} responseObject
 * @param {Object} prevResponse
 * @param {Object} metaFields
 * @returns {Object}
 */
export function createRecordFromResponse(
  responseObject,
  prevResponse,
  metaFields = {}
) {
  newResponse = new ResponseRecord(
    Immutable.fromJS({
      ...recordMerger(responseObject, new ResponseRecord()),
      reservedResponseType: responseObject._type
    })
  );
  newResponse = newResponse.merge(metaFields);

  newResponse
    .get("messages")
    .keySeq()
    .toArray()
    .forEach((key) => {
      newResponse.getIn(["messages", key]).forEach((m, index) => {
        let message = createMessageRecord(m);

        if (prevResponse) {
          // Merge in the previous message id to keep the id's stable across saves
          const prevMessage = prevResponse.getIn(["messages", key, index]);

          if (prevMessage) {
            message = message.merge({ id: prevMessage.id });
          }
        }

        newResponse = newResponse.setIn(
          ["messages", key, index],
          message
        );
      });
    });

  return newResponse;
}

/**
 * @param {Number} responseIndex
 * @param {Object} currentResponsesState
 * @returns {Boolean}
 */
export function checkIfDirty(
  responseIndex,
  currentResponsesState
) {
  const currentResponseState = currentResponsesState
    .getIn([responseIndex])
    .toJS();

  const lastResponseState = lastSavedResponses
    .getIn([lastSavedResponseIndex])
    .toJS();

  const fieldsToIgnore = [
    "isDirty",
    "isValid",
    "live",
    "quickReplyReference",
    "fallbackReference"
  ];

  // Delete fields we don't want to compare. This is kinda hacky though, eventually
  // Repsonse meta data should be kept separate from Response record
  fieldsToIgnore.forEach((field) => {
    delete currentResponseState[field];
    delete lastResponseState[field];
  });

  return !isEqual(currentResponseState, lastResponseState);
}

/**
 * @param {Number} responseIndex
 * @param {Object} state
 * @returns {Boolean}
 */
export function validateResponse(responseIndex, state) {
  const response = state.getIn([responseIndex]);
  const languagesArray = response.messages.keySeq().toArray();

  /** Return invalid if no languages */
  if (!languagesArray.length) {
    return false;
  }

  if (!response.handle) {
    return false;
  }

  return languagesArray.every((key) => {
    const messages = response.getIn(["messages", key]);

    return messages.every(m => !m.invalidFields.size);
  });
}

export const MESSAGE_RECORDS = {
  text: TextMessageRecord,
  shuffle: ShuffleMessageRecord,
  web_window: WebWindowMessageRecord,
  picture: PictureMessageRecord,
  video: VideoMessageRecord,
  link: LinkMessageRecord,
  custom_handoff_event: CustomHandoffEventMessageRecord,
  custom_javascript_event: JavascriptEventMessageRecord,
  capture: CaptureMessageRecord,
  handoff: HandoffMessageRecord,
  live_handoff: LiveHandoffMessageRecord,
  zendesk_live_handoff: ZendeskLiveHandoffMessageRecord,
  handoff_recipe: HandoffRecipeMessageRecord,
  helpdesk_handoff_recipe: ZendeskHandoffMessageRecord,
  http_request_recipe: HTTPMessageRecord,
  zendesk_agent_workspace_handoff: ZendeskAgentWorkspaceHandoffMessageRecord,
  base: HTTPMessageRecord, // base is a display_type override http_request_recipe
  sign_in: AuthMessageRecord,
  sign_out: DeauthMessageRecord,
  scheduled_block: ScheduledBlockRecordCreator,
  zendesk_handover: ZendeskChatHandoverRecord,
  liveperson_handover: LivepersonChatHandoverRecord,
  conditionals_block: ConditionalMessageRecordCreator,
  bot_switch: BotSwitchMessageRecord,
  scheduled_callback: ScheduledCallbackMessageRecord,
  survey: SatisfactionSurveyMessageRecord,
  nuance_secure_live_agent: SecureNuanceLiveAgentMessageRecord,
  zendesk_live_agent: ZendeskLiveAgentMessageRecord,
  salesforce_live_agent: SalesforceLiveAgentMessageRecord,
  ada_messenger_handoff: AdaMessengerMessageRecord,
  book_meeting: BookMeetingMessageRecord,
  book_meeting_recipe: BookMeetingMessageRecord,
  redirect_block: RedirectRecord,
  recaptcha: ReCaptchaRecord,
  list_selection_template: ListSelectionRecord,
  variable_override: VariableOverrideMessageRecord,
  widget: WidgetMessageRecord,
  shopify: ShopifyMessageRecord,
  salesforce: SalesforceMessageRecord,
  magento: MagentoMessageRecord,
  quick_replies_block: QuickRepliesMessageRecord,
  csat_survey: CsatMessageRecord,
  unsupported: UnsupportedMessageRecord
};

/**
 * Creates MessageRecord from message
 * @param {Object} message
 * @param {Boolean} shouldTransformFromAPI
 * @returns {Record} message record
 */
export function createMessageRecord(message, shouldTransformFromAPI = true) {
  // TODO: remove this
  if (!message) {
    return null;
  } // mainly for newly created schedule blocks

  const messageType = Immutable.fromJS(message).get("type");
  const messageDisplayType = Immutable.fromJS(message).get("displayType");
  const MessageRecordType = MESSAGE_RECORDS[messageDisplayType || messageType];
  let messageRecord;

  if (MessageRecordType) {
    messageRecord = new MessageRecordType(
      message,
      createMessageRecord,
      shouldTransformFromAPI
    ).merge({ id: uuid() });
  } else {
    messageRecord = new UnsupportedMessageRecord({
      id: uuid(),
      originalBlockType: messageType,
      originalMessage: message
    });
  }

  if (shouldTransformFromAPI) {
    messageRecord = fromAPI(messageRecord);
  }

  return messageRecord;
}

/**
 * @param {String} messageType
 * @returns {*}
 */
export function createEmptyMessageFromMessageType(messageType) {
  const MessageRecordType = MESSAGE_RECORDS[messageType];
  let messageRecord = new MessageRecordType({}).merge({ id: uuid() });
  messageRecord = fromAPI(messageRecord);

  return messageRecord;
}
